import { createContext, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { SLUG_CART } from '../../styles/slugs';
import { appendCommitHash } from '../../utils/appendHash';
import { AddressType } from '../api/queries/useAccountAddress';
import { ProductType } from '../api/queries/useProduct';
import { PriceType } from '../api/queries/useProducts';
import useVariantsByIds, {
  VariantOrderItem,
} from '../api/queries/useVariantsByIds';
import { VariantType } from '../api/queries/useVariantsByProductId';
import useLocalStorage from '../utils/useLocalStorage';

interface IContext {
  cart: Cart;
  setCart: (cart: Cart) => void;
  removeItem: (variantId: number) => void;
  decrementOne: (variantId: number) => void;
  incrementOne: (variantId: number) => void;
  addOne: (variantItem: VariantCartItem) => void;
  serverCartItems?: VariantOrderItem[];
  emptyCart: () => void;
  partiallyEmptyCart: (errors: OrderError[]) => void;
  verifyCart: () => void;
  orderErrors: OrderError[];
  setOrderErrors: (errors: OrderError[]) => void;
  setCartErrors: (errors: CartErrorType[]) => void;
  cartErrors: CartErrorType[];
  serverFetched: boolean;
}

const CartContext = createContext<IContext | undefined>(undefined);

export enum CartError {
  quantity,
  unavailable,
  price,
}

export type OrderError = {
  error: string;
  message: string;
  variantId: number;
  productName: string;
};

type CartErrorType = {
  error: CartError;
  variantId: number;
  variantName: string;
};

export type CartItemType = {
  quantity: number;
  dematerialized?: number;
  name: string;
  slug: string;
  variantId: number;
  price: PriceType;
  priceBefore?: PriceType;
};

export type Cart = {
  id: number;
  items: CartItemType[];
  totalMilesAmount: number;
  totalArticles: number;
  address?: AddressType;
};

export type VariantCartItem = Pick<VariantType, 'variantId'> &
  Pick<ProductType, 'price' | 'priceOverwrite' | 'name'> & {
    dematerialized: number;
    quantity: number;
    slug: string;
  };

const initialCart: Cart = {
  id: Math.random(),
  items: [],
  totalMilesAmount: 0,
  totalArticles: 0,
  address: undefined,
};

export const CartContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [cart, setCart] = useLocalStorage('cart', initialCart);
  const [cartErrors, setCartErrors] = useState<CartErrorType[]>([]);
  const [orderErrors, setOrderErrors] = useState<OrderError[]>([]);
  const navigate = useNavigate();

  const variantsIdList = useMemo(
    () => cart.items.map((item) => item.variantId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart.items.length],
  );

  const { data, isFetched } = useVariantsByIds({
    variantsIdList,
  });

  const serverCartItems = useMemo(() => {
    if (!data) return [];
    return data?.sort(
      (a, b) =>
        variantsIdList.indexOf(a.variantId) -
        variantsIdList.indexOf(b.variantId),
    );
  }, [data, variantsIdList]);

  /** check cart for errors */
  const verifyCart = async () => {
    const errors: CartErrorType[] = [];
    cart.items.forEach((item) => {
      const serverItem = serverCartItems.find(
        (serverItem) => serverItem.variantId === item.variantId,
      );

      if (!serverItem || serverItem.availableQuantity <= 0) {
        errors.push({
          error: CartError.unavailable,
          variantName: item.name,
          variantId: item.variantId,
        });
        return;
      }
      serverItem.availableQuantity < item.quantity &&
        errors.push({
          error: CartError.quantity,
          variantName: item.name,
          variantId: item.variantId,
        });

      const serverPrice = serverItem.priceOverwrite
        ? serverItem.priceOverwrite.milesAmount
        : serverItem.price.milesAmount;

      serverPrice !== item.price.milesAmount &&
        errors.push({
          error: CartError.price,
          variantName: item.name,
          variantId: item.variantId,
        });
    });
    if (errors.length) {
      setCartErrors(errors);
      navigate(appendCommitHash(`/${SLUG_CART}`));
    }
  };

  const removeItem = (variantId: number) => {
    const index = cart.items.findIndex((item) => item.variantId === variantId);

    if (index === -1) return;
    const tempCartItems = [...cart.items];
    tempCartItems.splice(index, 1);

    setCart({
      ...cart,
      totalArticles: tempCartItems
        .map((tci) => tci.quantity)
        .reduce((a, b) => a + b, 0),
      totalMilesAmount: tempCartItems
        .map((tci) => tci.price.milesAmount * tci.quantity)
        .reduce((a, b) => a + b, 0),
      items: tempCartItems,
    });
  };

  const incrementOne = (variantId: number) => {
    const index = cart.items.findIndex((line) => line.variantId === variantId);

    if (index === -1) return null;

    const cartItem = cart.items[index];
    const updatedCartItem = { ...cartItem, quantity: cartItem.quantity + 1 };
    let tempCartItems = [...cart.items];

    tempCartItems[index] = updatedCartItem;

    setCart({
      ...cart,
      totalArticles: tempCartItems
        .map((tci) => tci.quantity)
        .reduce((a, b) => a + b, 0),
      totalMilesAmount: tempCartItems
        .map((tci) => cartItem.price.milesAmount * tci.quantity)
        .reduce((a, b) => a + b, 0),
      items: tempCartItems,
    });
  };

  const addOne = (variantCartItem: VariantCartItem) => {
    const index = cart.items.findIndex(
      (line) => line.variantId === variantCartItem.variantId,
    );

    const cartItem =
      index === -1
        ? createCartItem(variantCartItem)
        : {
            ...cart.items[index],
            quantity: cart.items[index].quantity + variantCartItem.quantity,
          };

    let tempCartItems = [...cart.items];

    if (index === -1) tempCartItems.push(cartItem);
    else tempCartItems[index] = cartItem;
    setCart({
      ...cart,
      totalArticles: tempCartItems
        .map((tci) => tci.quantity)
        .reduce((a, b) => a + b, 0),
      totalMilesAmount: tempCartItems
        .map((tci) => tci.price.milesAmount * tci.quantity)
        .reduce((a, b) => a + b, 0),
      items: tempCartItems,
    });
  };

  const decrementOne = (variantId: number) => {
    const index = cart.items.findIndex((line) => line.variantId === variantId);

    if (index === -1) return;

    const cartItem = cart.items[index];
    if (cartItem.quantity - 1 === 0) return removeItem(variantId);

    const updatedCartItem = { ...cartItem, quantity: cartItem.quantity - 1 };

    let tempCartItems = [...cart.items];

    tempCartItems[index] = updatedCartItem;

    setCart({
      ...cart,
      totalArticles: tempCartItems
        .map((tci) => tci.quantity)
        .reduce((a, b) => a + b, 0),
      totalMilesAmount: tempCartItems
        .map((tci) => tci.price.milesAmount * tci.quantity)
        .reduce((a, b) => a + b, 0),
      items: tempCartItems,
    });
  };

  const emptyCart = () => setCart(initialCart);

  const partiallyEmptyCart = (errors: OrderError[]) => {
    const tempCart = { ...cart };
    tempCart.totalMilesAmount = 0;
    errors.forEach((error) => {
      const cartItem = cart.items.find(
        (item) => item.variantId === error.variantId,
      );
      const itemQuantity = cartItem?.quantity ?? 0;
      const itemPrice = cartItem?.price;
      tempCart.items = [
        ...tempCart.items.filter((item) => item.variantId === error.variantId),
      ];
      tempCart.totalArticles = tempCart.totalArticles - itemQuantity;
      tempCart.totalMilesAmount += itemQuantity * (itemPrice?.milesAmount ?? 0);
    });

    setCart(tempCart);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        serverCartItems,
        removeItem,
        addOne,
        decrementOne,
        incrementOne,
        emptyCart,
        partiallyEmptyCart,
        orderErrors,
        setOrderErrors,
        verifyCart,
        cartErrors,
        setCartErrors,
        serverFetched: isFetched,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context)
    throw new Error(
      'No CartContext.Provider found when calling useCartContext',
    );
  return context;
};

function createCartItem(variantCartItem: VariantCartItem): CartItemType {
  const price = variantCartItem.priceOverwrite
    ? variantCartItem.priceOverwrite
    : variantCartItem.price;

  const cartItem: CartItemType = {
    name: variantCartItem.name,
    slug: variantCartItem.slug,
    dematerialized: variantCartItem.dematerialized,
    quantity: variantCartItem.quantity,
    variantId: variantCartItem.variantId,
    price,
    priceBefore: variantCartItem.priceOverwrite
      ? variantCartItem.price
      : undefined,
  };

  return cartItem;
}
